<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import {
  DOCUMENT_TYPES,
  formatCpfCnpj,
  getDocumentType,
  isValidCpfCnpj,
  sanitizeCpfCnpj,
} from 'dashboard/helper/cpfCnpj';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['resolve']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const contactId = ref(null);
const resolveContext = ref(null);
const hasDocument = ref(false);
const documentNumber = ref('');
const isTouched = ref(false);
const isSaving = ref(false);
const hasSaveError = ref(false);

const isValid = computed(() => isValidCpfCnpj(documentNumber.value));

const message = computed(() => {
  if (hasSaveError.value) return t('CONVERSATION.CPF_CNPJ_DIALOG.SAVE_ERROR');
  if (isTouched.value && !isValid.value) {
    return t('CONVERSATION.CPF_CNPJ_DIALOG.INVALID');
  }
  if (!documentNumber.value) return '';

  return getDocumentType(documentNumber.value) === DOCUMENT_TYPES.CNPJ
    ? t('CONVERSATION.CPF_CNPJ_DIALOG.TYPE_CNPJ')
    : t('CONVERSATION.CPF_CNPJ_DIALOG.TYPE_CPF');
});

const hasError = computed(
  () => hasSaveError.value || (isTouched.value && !isValid.value)
);

const open = (contact, context = null) => {
  contactId.value = contact?.id;
  resolveContext.value = context;
  hasDocument.value = false;
  documentNumber.value = '';
  isTouched.value = false;
  hasSaveError.value = false;
  dialogRef.value?.open();
};

const resolve = () => {
  emit('resolve', resolveContext.value);
  dialogRef.value?.close();
};

const handleInput = event => {
  documentNumber.value = formatCpfCnpj(event.target.value);
  // Keeps the field in sync when the mask drops a character and the model does not change
  event.target.value = documentNumber.value;
  hasSaveError.value = false;
};

const saveAndResolve = async () => {
  isTouched.value = true;
  if (!isValid.value) return;

  isSaving.value = true;
  try {
    await store.dispatch('contacts/update', {
      id: contactId.value,
      cpfCnpj: sanitizeCpfCnpj(documentNumber.value),
    });
    resolve();
  } catch (error) {
    hasSaveError.value = true;
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('CONVERSATION.CPF_CNPJ_DIALOG.TITLE')"
    @confirm="saveAndResolve"
  >
    <Input
      v-if="hasDocument"
      :model-value="documentNumber"
      :label="t('CONVERSATION.CPF_CNPJ_DIALOG.LABEL')"
      :placeholder="t('CONVERSATION.CPF_CNPJ_DIALOG.PLACEHOLDER')"
      :message="message"
      :message-type="hasError ? 'error' : 'info'"
      autofocus
      @input="handleInput"
      @blur="isTouched = true"
    />
    <template #footer>
      <div v-if="hasDocument" class="flex items-center w-full gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          class="w-full"
          type="button"
          @click="dialogRef.close()"
        />
        <Button
          color="blue"
          :label="t('CONVERSATION.CPF_CNPJ_DIALOG.SAVE_AND_RESOLVE')"
          class="w-full"
          type="submit"
          :is-loading="isSaving"
          :disabled="!isValid || isSaving"
        />
      </div>
      <div v-else class="flex items-center w-full gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="t('CONVERSATION.CPF_CNPJ_DIALOG.NO')"
          class="w-full"
          type="button"
          @click="resolve"
        />
        <Button
          color="blue"
          :label="t('CONVERSATION.CPF_CNPJ_DIALOG.YES')"
          class="w-full"
          type="button"
          @click="hasDocument = true"
        />
      </div>
    </template>
  </Dialog>
</template>
